import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Translation } from '../translations';
import { supabase } from '../services/supabase';
import { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import LanguageToggle from './LanguageToggle';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { Shield } from 'lucide-react';

interface NavigationProps {
  t: Translation;
  currentLang?: string;
  onLanguageChange?: (lang: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ t, currentLang, onLanguageChange }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { isAdmin } = useAdminStatus();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Check if user is logged in
    checkUser();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      setUser(session?.user || null);
    }) || { data: { subscription: null } };
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      subscription?.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    if (supabase) {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    }
  };

  const handleLogout = async () => {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return;
      }

      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        alert('로그아웃 중 오류가 발생했습니다. 다시 시도해주세요.');
        return;
      }

      setUser(null);
      
      // 현재 위치가 홈페이지가 아닌 경우에만 navigate 사용
      // 홈페이지인 경우 페이지 새로고침
      if (window.location.pathname === '/') {
        window.location.reload();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Unexpected logout error:', error);
      alert('로그아웃 처리 중 오류가 발생했습니다.');
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-xl' : 'bg-white/80 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center group cursor-pointer">
              <img 
                src="/aivuebiz_logo.png" 
                alt="AivueBiz Logo" 
                className="h-12 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-10">
            <a href="#features" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">{t.nav.features}</a>
            <a href="/#pricing" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">{t.nav.pricing}</a>
            <a href="/about" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">{t.nav.about}</a>
            <a href="/contact" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">{t.nav.contact}</a>
          </div>
          
          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {currentLang && onLanguageChange && (
              <LanguageToggle currentLang={currentLang} onLanguageChange={onLanguageChange} />
            )}
            {user ? (
              <>
                <a href="/dashboard" className="px-5 py-2.5 text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">대시보드</a>
                {isAdmin && (
                  <a 
                    href="/dashboard/mgmt" 
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-lg transition-all duration-200 font-medium border border-red-200"
                  >
                    <Shield className="w-4 h-4" />
                    <span>관리자</span>
                  </a>
                )}
                <button onClick={handleLogout} className="px-5 py-2.5 text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">로그아웃</button>
              </>
            ) : (
              <a href="/login" className="px-5 py-2.5 text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer">{t.nav.login}</a>
            )}
            <a href="/download" className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 font-semibold cursor-pointer">
              {t.nav.download}
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="px-4 py-2 space-y-1">
            <a href="#features" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">{t.nav.features}</a>
            <a href="/#pricing" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">{t.nav.pricing}</a>
            <a href="/about" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">{t.nav.about}</a>
            <a href="/contact" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">{t.nav.contact}</a>
            <div className="pt-4 pb-3 border-t border-gray-200">
              {user ? (
                <>
                  <a href="/dashboard" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">대시보드</a>
                  {isAdmin && (
                    <a 
                      href="/dashboard/mgmt" 
                      className="flex items-center space-x-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 font-medium"
                    >
                      <Shield className="w-5 h-5" />
                      <span>관리자</span>
                    </a>
                  )}
                  <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">로그아웃</button>
                </>
              ) : (
                <a href="/login" className="block px-3 py-2 text-gray-600 cursor-pointer hover:text-primary-600">{t.nav.login}</a>
              )}
              <a href="/download" className="block px-3 py-2 mt-2 gradient-primary text-white rounded-lg text-center cursor-pointer hover:shadow-lg">
                {t.nav.download}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;